import React, { forwardRef, useState } from "react";
import { IconArrowLeft } from "@purpur/icon/arrow-left";
import { IconArrowRight } from "@purpur/icon/arrow-right";
import { VisuallyHidden } from "@purpur/visually-hidden";
import c from "classnames/bind";

import { navigateToPage } from "./navigation";
import styles from "./pagination.module.scss";
import { PaginationPageSelector } from "./pagination-page-selector";
import { PaginationPageSizeSelector } from "./pagination-page-size-selector";
import { PaginationPages } from "./pagination-pages";
import { PaginationStepTrigger } from "./pagination-step-trigger";
import { type LinkProps, type NoLinkProps } from "./types";
import { usePaginationPages } from "./use-pagination-pages.hook";

const cx = c.bind(styles);

export type PaginationTexts = {
  nextButtonAriaLabel: string;
  /**
   *  Omiting this and previousButtonText will result in an icon-only button.
   */
  nextButtonText?: string;
  /**
   * This is the divider word between the current page and the total number of pages.
   * For example, if the current page is 1 and the total number of pages is 10, the text will be "1 of 10".
   */
  outOfLabel: string;
  /**
   * This is a hidden label for the page selector
   */
  pageSelectorLabel: string;
  /**
   * This is the "..." text that is displayed when there are more pages than can be shown at once.
   */
  truncationSeparatorAriaLabel?: string;
  previousButtonAriaLabel: string;
  /**
   *  Omiting this and nextButtonText will result in an icon-only button.
   */
  previousButtonText?: string;
  /**
   * This is a screen reader text that will be inserted before the current page number. For example, for step 1 it will say "Go to step 1".
   */
  stepNumberPrefix: string;
};

export type PageSizeProps = {
  availablePageSizes: number[];
  pageSize?: number;
  /**
   * The label describing the page size select.
   */
  pageSizeLabel: string;
};

export type NoPageSizeProps = {
  availablePageSizes?: never;
  pageSize?: number;
  pageSizeLabel?: never;
};

export type PaginationProps = {
  currentPage: number;
  pageSelectorId?: string;
  totalItems: number;
} & PaginationTexts &
  (PageSizeProps | NoPageSizeProps) &
  (LinkProps | NoLinkProps);

const rootClassName = "purpur-pagination";

export const Pagination = forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      ["data-testid"]: dataTestId = "purpur-pagination",
      linkElement = "a",
      asLink,
      availablePageSizes,
      className,
      currentPage = 1,
      hrefGetter,
      navigationFunction,
      nextButtonAriaLabel,
      nextButtonText,
      onPageChange,
      outOfLabel,
      pageSelectorId,
      pageSelectorLabel,
      pageSize = 10,
      pageSizeLabel,
      previousButtonAriaLabel,
      previousButtonText,
      stepNumberPrefix,
      totalItems,
      truncationSeparatorAriaLabel,
      ...props
    },
    ref
  ) => {
    const classes = cx(className, rootClassName);
    const [_pageSize, _setPageSize] = useState(pageSize);
    const { pages, numberOfPages } = usePaginationPages(totalItems, _pageSize, currentPage);
    const isIconOnly = !previousButtonText && !nextButtonText;

    const _onPageChange = (page: number): void => {
      onPageChange?.({ currentPage: page, pageSize });
    };

    const _hrefGetter = (page: number): string => {
      return hrefGetter?.({ page, pageSize }) ?? "";
    };

    const _onPageSizeChange = (newPageSize: number): void => {
      _setPageSize(newPageSize);
      onPageChange?.({ currentPage: 1, pageSize: newPageSize });
    };

    const _navigationFunction = (page: number, url: string): void => {
      if (navigationFunction) {
        navigationFunction({ currentPage: page, pageSize: _pageSize, url });
      } else {
        navigateToPage(url);
      }
    };

    const linkProps = asLink
      ? { asLink: true as const, hrefGetter: _hrefGetter }
      : {
          asLink: undefined as never,
          hrefGetter: undefined as never,
        };

    const ContainerTag = asLink ? "nav" : "div";

    if (!numberOfPages) {
      return null;
    }

    return (
      <ContainerTag className={classes} data-testid={dataTestId} ref={ref} {...props}>
        <div
          className={cx(`${rootClassName}__pagination-container`, {
            [`${rootClassName}__pagination-container--with-page-size-select`]: !!availablePageSizes,
          })}
        >
          <div className={cx(`${rootClassName}__page-navigation-container`)}>
            <PaginationStepTrigger
              {...linkProps}
              linkElement={linkElement}
              aria-label={previousButtonAriaLabel}
              className={cx(`${rootClassName}__step-trigger`)}
              data-testid={`${dataTestId}-previous-step-trigger`}
              disabled={currentPage === 1}
              onPageChange={_onPageChange}
              pageToNavigateTo={currentPage - 1}
              isIconOnly={isIconOnly}
            >
              <IconArrowLeft size="sm" />
              <span
                className={cx(`${rootClassName}__step-trigger-label`, {
                  [`${rootClassName}__step-trigger-label--not-icon-only`]: !isIconOnly,
                })}
              >
                {previousButtonText}
              </span>
              {currentPage !== 1 && (
                <VisuallyHidden data-testid={`${dataTestId}-previous-step-trigger-hidden-text`}>
                  {`${stepNumberPrefix} ${currentPage - 1}`}
                </VisuallyHidden>
              )}
            </PaginationStepTrigger>
            <div className={cx(`${rootClassName}__page-trigger-container`)}>
              <PaginationPageSelector
                {...linkProps}
                className={cx(`${rootClassName}__page-selector`, {
                  [`${rootClassName}__page-selector--visible`]: pages.length > 3,
                })}
                currentPage={currentPage}
                data-testid={`${dataTestId}-page-selector`}
                navigationFunction={_navigationFunction}
                numberOfPages={numberOfPages}
                onPageChange={_onPageChange}
                outOfLabel={outOfLabel}
                pageSelectorId={pageSelectorId}
                pageSelectorLabel={pageSelectorLabel}
              />
              <PaginationPages
                {...linkProps}
                linkElement={linkElement}
                className={cx(`${rootClassName}__pages`, {
                  [`${rootClassName}__pages--visible`]: pages.length <= 3,
                })}
                currentPage={currentPage}
                data-testid={`${dataTestId}-pages`}
                stepNumberPrefix={stepNumberPrefix}
                onPageChange={_onPageChange}
                truncationSeparatorAriaLabel={truncationSeparatorAriaLabel}
                pages={pages}
              />
            </div>
            <PaginationStepTrigger
              {...linkProps}
              linkElement={linkElement}
              aria-label={nextButtonAriaLabel}
              className={cx(`${rootClassName}__step-trigger`)}
              data-testid={`${dataTestId}-next-step-trigger`}
              disabled={currentPage === numberOfPages}
              onPageChange={_onPageChange}
              pageToNavigateTo={currentPage + 1}
              isIconOnly={isIconOnly}
            >
              <span
                className={cx(`${rootClassName}__step-trigger-label`, {
                  [`${rootClassName}__step-trigger-label--not-icon-only`]: !isIconOnly,
                })}
              >
                {nextButtonText}
              </span>
              {currentPage !== numberOfPages && (
                <VisuallyHidden data-testid={`${dataTestId}-next-step-trigger-hidden-text`}>
                  {`${stepNumberPrefix} ${currentPage + 1}`}
                </VisuallyHidden>
              )}
              <IconArrowRight size="sm" />
            </PaginationStepTrigger>
          </div>
          {availablePageSizes && (
            <div className={cx(`${rootClassName}__page-size-select-container`)}>
              <PaginationPageSizeSelector
                {...(asLink
                  ? { asLink: true, hrefGetter, navigationFunction }
                  : { asLink: undefined as never, hrefGetter: undefined as never })}
                availablePageSizes={availablePageSizes}
                className={cx(`${rootClassName}__page-size-selector`)}
                data-testid={`${dataTestId}-page-size-selector`}
                onPageSizeChange={_onPageSizeChange}
                pageSize={_pageSize}
                pageSizeLabel={pageSizeLabel}
              />
            </div>
          )}
        </div>
      </ContainerTag>
    );
  }
);

Pagination.displayName = "Pagination";
